import {type LocationPdfType, PDF_MAP_PIXEL_SIZE, type PdfOrientation} from "../redux/pdfConstants";
import {selectPdfPreview} from "../redux/pdfPreviewSlice";
import {useRevaSelector} from "../redux/revaHooks";

/**
 * Ergebnis-Format, das direkt als {@code pdfPreview}-Prop an die
 * {@code OpenLayersMap} durchgereicht wird.
 */
export interface PdfPreviewInfo {
    /** logische Breite des Karten-Ausschnitts in Pixeln */
    pixelWidth: number;
    /** logische Höhe des Karten-Ausschnitts in Pixeln */
    pixelHeight: number;
    /** Re-Trigger-Zähler; jede Erhöhung blendet die Vorschau-Box neu ein */
    nonce: number;
    /** true, während gerade das PDF für diesen Typ generiert wird (Box „atmet"). */
    generating: boolean;
    /** Karten-Typ des zugehörigen Export-Buttons — für den Klick in der Box. */
    type: LocationPdfType;
    /** Ausrichtung oder null — für den Klick in der Box. */
    orientation: PdfOrientation | null;
    /**
     * Ein Klick-Button je Karten-Typ, den die Box anzeigen soll (siehe {@link usePdfPreview}s
     * {@code labels}-Parameter). Bei nur einem Typ ein einzelner Eintrag ohne eigene
     * Beschriftung ("PDF jetzt erstellen").
     */
    actions: Array<{type: LocationPdfType; orientation: PdfOrientation | null; label: string}>;
}

/**
 * Liest den PDF-Preview-Slice und liefert — falls der zuletzt überfahrene
 * Export-Button zum übergebenen {@code type} passt — die logischen Pixelmaße
 * des Karten-Ausschnitts samt aktuellem {@code nonce}, sonst {@code null}.
 *
 * Die Pixelmaße ergeben sich aus {@link PDF_MAP_PIXEL_SIZE} nach der im Slice
 * gespeicherten Orientierung; ohne Orientierung (Overview, Traffic, Nearby)
 * wird der {@code default}-Eintrag verwendet.
 *
 * Für den POI-Tab (eine Karte, die zwischen Traffic/Nearby umschaltet) ruft
 * der Aufrufer den Hook mit dem gerade sichtbaren Typ auf
 * ({@code showTrafficMap ? "traffic" : "nearby"}).
 *
 * Mehrere Export-Buttons können dieselbe Karte teilen (z.B. Übersichts- und
 * Lage-Kartenset-Button auf der Adressseite) — dafür akzeptiert {@code type}
 * auch ein Array von Typen.
 *
 * Teilen sich mehrere Typen eine Karte, zeigt die Box für jeden Typ einen eigenen
 * Klick-Button ({@link PdfPreviewInfo.actions}) — die Beschriftung kommt aus {@code labels}
 * (Typ -> Text, z.B. {@code {overview: "Übersicht", location: "Kartenset"}}).
 *
 * @param type der Karten-Typ (oder mehrere), auf dessen Export-Button reagiert werden soll
 * @param labels Beschriftung je Typ für die Klick-Buttons in der Box; ohne Eintrag "PDF jetzt erstellen"
 * @return Pixelmaße + nonce oder {@code null}, wenn kein passender Hover vorliegt
 */
export function usePdfPreview(
    type: LocationPdfType | LocationPdfType[],
    labels?: Partial<Record<LocationPdfType, string>>,
): PdfPreviewInfo | null {
    const preview = useRevaSelector(selectPdfPreview);
    const types = Array.isArray(type) ? type : [type];

    if (preview.type === null || preview.nonce === 0 || !types.includes(preview.type)) {
        return null;
    }

    const size = (preview.orientation && PDF_MAP_PIXEL_SIZE[preview.orientation]) || PDF_MAP_PIXEL_SIZE.default;

    return {
        pixelWidth: size.width,
        pixelHeight: size.height,
        nonce: preview.nonce,
        generating: preview.generating,
        type: preview.type,
        orientation: preview.orientation,
        actions: types.map((t) => ({
            type: t,
            orientation: preview.orientation,
            label: labels?.[t] ?? "PDF jetzt erstellen",
        })),
    };
}
